import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  isFloat,
  timeToFloat,
  formatTime,
  removeCommas,
  splitAmountAndWage,
  sanitizeInputValue,
  convertAutomatically,
  containingArithmeticOperation,
  NUMBER_OR_TIME_REGEX
} from './utilities';

const formatValue = (value, isTimeFormat, useTimeFormat) => {
  if (isFloat(value) || !NUMBER_OR_TIME_REGEX.test(value)) {
    return value;
  } else {
    const { wage, amount } = splitAmountAndWage(value);

    const formattedAmount = useTimeFormat
      ? isTimeFormat
        ? formatTime(amount)
        : timeToFloat(amount)
      : amount;

    return `${wage}*${formattedAmount}`;
  }
};

class NumberOrTimePlusInput extends Component {
  constructor(props) {
    super(props);
    this.state = {
      displayValue: formatValue(
        props.value,
        props.isTimeFormat,
        props.useTimeFormat
      ),
      internalValue: props.value,
      isTyping: false,
    };

    this.debounceTimeout = null;
    this.isComposing = false;
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (
      nextProps.value !== prevState.internalValue ||
      nextProps.isTimeFormat !== prevState.isTimeFormat ||
      nextProps.useTimeFormat !== prevState.useTimeFormat
    ) {
      if (!prevState.isTyping) {
        const displayValue = formatValue(nextProps.value, nextProps.isTimeFormat, nextProps.useTimeFormat);

        return {
          displayValue,
          internalValue: nextProps.value,
          isTimeFormat: nextProps.isTimeFormat,
          useTimeFormat: nextProps.useTimeFormat,
        };
      } else {
        return {
          isTimeFormat: nextProps.isTimeFormat,
          useTimeFormat: nextProps.useTimeFormat,
        };
      }
    }

    return null;
  }

  componentWillUnmount() {
    clearTimeout(this.debounceTimeout);
  }

  handleCompositionStart = () => {
    // #2250: IME(全角)入力を許容する。変換中はそのまま表示し、確定(compositionend)で
    // 半角へ自動変換 + 不許可文字を削除する。警告トーストは出さない。
    this.isComposing = true;
  };

  handleCompositionEnd = (event) => {
    this.isComposing = false;
    this.handleInputChange(event);
  };

  handleInputChange = (event) => {
    // While the IME is composing, show the raw value without converting.
    // Converting full-width to half-width and writing it back into the
    // controlled input mid-composition makes the IME re-insert its buffer,
    // which duplicates the characters (e.g. "２４" -> "2244"). The
    // conversion runs once on compositionend instead.
    if (this.isComposing) {
      clearTimeout(this.debounceTimeout);
      this.setState({ displayValue: event.target.value, isTyping: true });

      this.debounceTimeout = setTimeout(() => {
        this.setState({ isTyping: false });
      }, 500);

      return;
    }

    // #2250: 全角→半角へ変換し、単価×時間/式欄の許可文字以外を削除する。
    let value = removeCommas(event.target.value);

    value = convertAutomatically(value, this.props.inputType);
    value = sanitizeInputValue(value, this.props.isTimeFormat);

    clearTimeout(this.debounceTimeout);

    this.setState({ displayValue: value, isTyping: true });

    if (isFloat(value) || containingArithmeticOperation(value)) {
      // 変換・削除後の値を確実に親へ渡すため event.target.value を上書きする。
      event.target.value = value;
      this.setState({ internalValue: value }, () => {
        this.props.onChange(event);
      });
    }

    this.debounceTimeout = setTimeout(() => {
      this.setState({ isTyping: false });
    }, 500);
  };

  handleBlur = (event) => {
    const value = this.state.internalValue || this.props.value;
    const formattedValue = formatValue(
      value,
      this.props.isTimeFormat,
      this.props.useTimeFormat
    );

    this.setState({ displayValue: formattedValue }, () => {
      this.props.onChange(event);
    });
  };

  render() {
    // onImeWarning は内部用コールバックなので DOM input へは渡さない。
    const { onImeWarning, ...inputProps } = this.props;

    return (
      <input
        {...inputProps}
        onBlur={this.handleBlur}
        onChange={this.handleInputChange}
        onCompositionEnd={this.handleCompositionEnd}
        onCompositionStart={this.handleCompositionStart}
        value={this.state.displayValue}
      />
    );
  }
}

NumberOrTimePlusInput.propTypes = {
  inputType: PropTypes.string.isRequired,
  isTimeFormat: PropTypes.bool,
  useTimeFormat: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onImeWarning: PropTypes.func,
};

NumberOrTimePlusInput.defaultProps = {
  isTimeFormat: false,
  useTimeFormat: false,
  value: '',
};

export default NumberOrTimePlusInput;
